import '../../styles/Auth.css';

import {
  faEye,
  faEyeSlash,
  faUserShield,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {createUserWithEmailAndPassword} from 'firebase/auth';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';

import {apiService} from '../../apiService';
import {auth} from '../../firebase';

// Auto-capitalize each word in a name
const capitalizeName = (name) =>
  name.replace(/\b[a-z]/g, (char) => char.toUpperCase());

const Signup = () => {
  const [organizationCodes, setOrganizationCodes] = useState(['']);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const updateOrgCode = (index, value) => {
    setOrganizationCodes((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addOrgCode = () => {
    setOrganizationCodes((prev) => [...prev, '']);
  };

  const removeOrgCode = (index) => {
    setOrganizationCodes((prev) => prev.filter((_, i) => i !== index));
  };

  // Validation functions
  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const validateName = (name) => {
    const regex = /^[A-Za-z\s]+$/;
    return regex.test(name);
  };

  const validatePhone = (phone) => {
    // Allow formats like: 1234567890, 123-456-7890, (123) 456-7890, etc.
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const formatPhoneNumber = (phone) => {
    return phone.replace(/\D/g, '');
  };

  const validateOrganizationCode = async (code) => {
    try {
      const data = await apiService.post('/api/verify-org-code/', {
        company_code: code,
        admin: isAdmin ? 1 : 0, // Add admin parameter based on selection
      });

      return data.valid;
    } catch (error) {
      console.error('Error verifying org code:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate all organization codes
    const codes = organizationCodes.map((c) => c.trim()).filter(Boolean);
    if (codes.length === 0) {
      setError('At least one organization code is required.');
      setLoading(false);
      return;
    }

    // Check for duplicates
    const uniqueCodes = new Set(codes);
    if (uniqueCodes.size !== codes.length) {
      setError('Duplicate organization codes are not allowed.');
      setLoading(false);
      return;
    }

    for (const code of codes) {
      const isValid = await validateOrganizationCode(code);
      if (!isValid) {
        setError(`Invalid organization code: ${code}`);
        setLoading(false);
        return;
      }
    }

    if (!validateName(firstName)) {
      setError('First name should contain only letters.');
      setLoading(false);
      return;
    }

    if (!validateName(lastName)) {
      setError('Last name should contain only letters.');
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    if (phoneNumber && !validatePhone(phoneNumber)) {
      setError('Phone number must be a valid 10-digit number.');
      setLoading(false);
      return;
    }

    try {
      // Step 1: Create user with Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Step 2: Register user in the backend using the register_manager endpoint
      try {
        await apiService.post('/api/register-manager/', {
          comp_codes: codes.map((c) => parseInt(c)),
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone_number: formatPhoneNumber(phoneNumber),
          uid: user.uid,
        });

        // Store user data in localStorage for session persistence
        const userData = {
          uid: user.uid,
          firstName,
          lastName,
          isAdmin,
        };
        localStorage.setItem('userData', JSON.stringify(userData));

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (backendError) {
        // If backend registration fails, delete the Firebase user to maintain consistency
        try {
          await user.delete();
        } catch (deleteError) {
          console.error(
            'Failed to delete Firebase user after backend registration failure:',
            deleteError,
          );
        }

        setError(
          'Registration failed: ' +
            (backendError.response?.data?.error || backendError.message),
        );
        console.error('Backend registration error:', backendError);
      }
    } catch (firebaseError) {
      setError(getErrorMessage(firebaseError.code));
      console.error('Firebase signup error:', firebaseError);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      default:
        return 'An error occurred during signup. Please try again.';
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className='auth-container'>
      <div className='auth-card'>
        <h2>Sign Up to Tenvos</h2>

        {error && <div className='auth-error'>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Account Type Selection */}
          <div className='form-group'>
            <label>Account Type</label>
            <div className='account-type-container'>
              <div
                className={`account-type-option ${isAdmin ? 'selected' : ''}`}
                onClick={() => {
                  setIsAdmin(true);
                  setOrganizationCodes(['']);
                }}
              >
                <FontAwesomeIcon
                  icon={faUserShield}
                  className='account-type-icon'
                />
                <span>Admin</span>
                <div className={`radio-button ${isAdmin ? 'selected' : ''}`}>
                  {isAdmin && <div className='radio-button-inner'></div>}
                </div>
              </div>
              <div
                className={`account-type-option ${!isAdmin ? 'selected' : ''}`}
                onClick={() => setIsAdmin(false)}
              >
                <FontAwesomeIcon
                  icon={faUserTie}
                  className='account-type-icon'
                />
                <span>Manager</span>
                <div className={`radio-button ${!isAdmin ? 'selected' : ''}`}>
                  {!isAdmin && <div className='radio-button-inner'></div>}
                </div>
              </div>
            </div>
            <div className='helper-text'>
              {/* {isAdmin ? 
                'Admins can only register for master organizations' : 
                'Managers can only register for sub-organizations'
              } */}
            </div>
          </div>

          <div className='form-group'>
            <label>
              {isAdmin ? 'Master Organization Code' : 'Sub Organization Code(s)'}
            </label>
            {organizationCodes.map((code, index) => (
              <div key={index} className='org-code-row'>
                <input
                  type='text'
                  value={code}
                  onChange={(e) => updateOrgCode(index, e.target.value)}
                  required
                  disabled={loading}
                  placeholder={
                    isAdmin
                      ? 'Enter master organization code'
                      : 'Enter sub organization code'
                  }
                />
                {!isAdmin && organizationCodes.length > 1 && (
                  <button
                    type='button'
                    className='remove-org-code-btn'
                    onClick={() => removeOrgCode(index)}
                    disabled={loading}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            {!isAdmin && (
              <button
                type='button'
                className='add-org-code-btn'
                onClick={addOrgCode}
                disabled={loading}
              >
                + Add Another Sub Organization
              </button>
            )}
            <div className='helper-text org-code-note'>
              {isAdmin
                ? 'Do not share this code with non-admin managers'
                : 'Do not share this code with your employees'}
            </div>
          </div>

          <div className='form-group'>
            <label htmlFor='first-name'>First Name</label>
            <input
              type='text'
              id='first-name'
              value={firstName}
              onChange={(e) => setFirstName(capitalizeName(e.target.value))}
              required
              disabled={loading}
              placeholder='Enter your first name'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='last-name'>Last Name</label>
            <input
              type='text'
              id='last-name'
              value={lastName}
              onChange={(e) => setLastName(capitalizeName(e.target.value))}
              required
              disabled={loading}
              placeholder='Enter your last name'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='email'>Email</label>
            <input
              type='email'
              id='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder='Enter your email address'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='phone'>Phone Number</label>
            <input
              type='tel'
              id='phone'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={loading}
              placeholder='Enter your 10-digit phone number'
            />
          </div>

          <div className='form-group'>
            <label htmlFor='password'>Password</label>
            <div className='password-input-container'>
              <input
                type={showPassword ? 'text' : 'password'}
                id='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder='Create a password (min. 6 characters)'
              />
              <button
                type='button'
                className='password-toggle-btn'
                onClick={togglePasswordVisibility}
                tabIndex='-1'
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          <button type='submit' className='auth-button' disabled={loading}>
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className='auth-links'>
          <Link to='/login' className='auth-link'>
            Already have an account? Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
